import { ReactNode } from 'react'
import cx from 'classnames'
import { GlassSurface, Stack, Text } from '~m/designSystem'
import styles from './SyncResult.module.css'

export interface SyncResultStat {
  /** The number or value */
  value: ReactNode
  /** The caption below the value */
  label: ReactNode
}

export interface SyncResultProps {
  /** Headline confirming the outcome */
  title: ReactNode
  /** Supporting sentence under the title */
  description?: ReactNode
  /** Icon shown in the success mark (e.g. a tick) */
  icon?: ReactNode
  /** Outcome figures, e.g. how many were created and linked */
  stats?: SyncResultStat[]
  /** Actions, e.g. a "run another sync" button */
  children?: ReactNode
  /** Additional CSS class */
  className?: string
}

/**
 * The confirmation screen shown once a sync has been applied,
 * summarising the outcome with a success mark and GlassSurface
 * stat tiles.
 */
export function SyncResult({
  title,
  description,
  icon,
  stats = [],
  children,
  className,
}: SyncResultProps) {
  return (
    <Stack
      alignItems="center"
      gap={2}
      className={cx(styles.SyncResult, className)}
    >
      {icon && (
        <div className={styles.SyncResult__mark}>{icon}</div>
      )}
      <Text variant="page-title" component="h2">
        {title}
      </Text>
      {description && (
        <Text
          variant="body"
          component="p"
          className={styles.SyncResult__sub}
        >
          {description}
        </Text>
      )}
      {stats.length > 0 && (
        <Stack direction="row" gap={1.5} justifyContent="center">
          {stats.map((stat, i) => (
            <GlassSurface
              key={i}
              variant="card"
              className={styles.SyncResult__stat}
            >
              <Text variant="section-title" component="div">
                {stat.value}
              </Text>
              <Text variant="field-label" component="div">
                {stat.label}
              </Text>
            </GlassSurface>
          ))}
        </Stack>
      )}
      {children}
    </Stack>
  )
}
